// src/components/ControleLogs.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import './ControleLogs.css';
import {
  FaFileCsv, FaChevronLeft, FaChevronRight,
  FaSearch, FaTimes, FaFilter,
} from 'react-icons/fa';

// Chips de categoria — cada um é um termo "fuzzy" que casa via LIKE no backend
const CATEGORIAS = [
  { label: 'Login',    termo: 'LOGIN' },
  { label: 'Logout',   termo: 'LOGOUT' },
  { label: 'Senha',    termo: 'SENHA' },
  { label: 'MFA',      termo: 'MFA' },
  { label: 'Exclusão', termo: 'EXCL' },
  { label: 'Bloqueio', termo: 'BLOQUE' },
];

// Classifica a ação numa variante visual (cor) por palavra-chave
const getAcaoVariant = (acao = '') => {
  const a = String(acao).toUpperCase();
  if (/(SUCESSO|CRIAD|RESTAURAD|DESBLOQUE|ATIVAD|CONCLU|APROVAD)/.test(a)) return 'success';
  if (/(FALHA|EXCL|REMOV|BLOQUE|NEGAD|DELET|DEFINITIVA|REPROVAD)/.test(a)) return 'danger';
  if (/(SENHA|MFA|RESET|ALTERAD|ATUALIZ|EDIT|IMPERSON)/.test(a)) return 'warning';
  if (/(LOGIN|LOGOUT|LOGOFF|ACESSO|PREVIEW|ENVIAD)/.test(a)) return 'info';
  return 'neutral';
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Realça o termo buscado dentro do texto da célula
const realcar = (texto, termo) => {
  if (!termo || texto === null || texto === undefined) return texto ?? '';
  const str = String(texto);
  const partes = str.split(new RegExp(`(${escapeRegex(termo)})`, 'ig'));
  return partes.map((p, i) =>
    p.toLowerCase() === termo.toLowerCase()
      ? <mark key={i} className="log-highlight">{p}</mark>
      : p,
  );
};

export default function ControleLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtros
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Paginação (mantida)
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalDePaginas, setTotalDePaginas] = useState(0);
  const [totalDeLogs, setTotalDeLogs] = useState(0);

  const buscaInputRef = useRef(null);

  // Debounce da busca: aplica o termo e volta pra página 1
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaDebounced(busca.trim());
      setPaginaAtual(1);
    }, 400);
    return () => clearTimeout(t);
  }, [busca]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (buscaDebounced) params.append('q', buscaDebounced);
      params.append('pagina', paginaAtual);

      const { data } = await api.get(`/api/logs?${params.toString()}`);

      setLogs(data.logs);
      setTotalDeLogs(data.totalLogs);
      setTotalDePaginas(data.totalPages);
      setPaginaAtual(data.currentPage);
      setError('');
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
      setError('Falha ao carregar os logs. Você pode não ter permissão.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, buscaDebounced, paginaAtual]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Atalhos: "/" foca a busca, "Esc" limpa (quando focado nela)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement !== buscaInputRef.current) {
        e.preventDefault();
        buscaInputRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === buscaInputRef.current) {
        setBusca('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const irParaPaginaAnterior = () => { if (paginaAtual > 1) setPaginaAtual(paginaAtual - 1); };
  const irParaPaginaSeguinte = () => { if (paginaAtual < totalDePaginas) setPaginaAtual(paginaAtual + 1); };

  const handleDataInicio = (v) => { setDataInicio(v); setPaginaAtual(1); };
  const handleDataFim    = (v) => { setDataFim(v); setPaginaAtual(1); };

  const aplicarCategoria = (termo) => {
    setBusca((atual) => (atual.trim().toUpperCase() === termo ? '' : termo));
    buscaInputRef.current?.focus();
  };

  const limparTudo = () => {
    setBusca('');
    setDataInicio('');
    setDataFim('');
    setPaginaAtual(1);
  };

  const temFiltros = !!(buscaDebounced || dataInicio || dataFim);
  const categoriaAtiva = useMemo(() => busca.trim().toUpperCase(), [busca]);

  const handleExportar = async () => {
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (buscaDebounced) params.append('q', buscaDebounced);
      params.append('exportar', 'true');

      const response = await api.get(`/api/logs?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const hoje = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `relatorio_logs_${hoje}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      alert('Não foi possível exportar os logs. Verifique se há dados no período selecionado.');
    }
  };

  const formatarData = (timestamp) =>
    new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

  return (
    <div className="logs-container">
      <div className="logs-header">
        <div>
          <h1 className="logs-title">Controle de Logs</h1>
          <p className="logs-subtitle">Auditoria de eventos e atividades do sistema</p>
        </div>
        {!loading && !error && (
          <span className="logs-count-badge">{totalDeLogs.toLocaleString('pt-BR')} registros</span>
        )}
      </div>

      {/* TOOLBAR */}
      <div className="logs-toolbar">
        <div className="toolbar-row">
          <div className="search-field">
            <FaSearch className="search-field__icon" />
            <input
              ref={buscaInputRef}
              type="text"
              className="search-field__input"
              placeholder="Buscar por usuário, ação ou detalhe…  ( / para focar )"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            {busca && (
              <button className="search-field__clear" onClick={() => setBusca('')} aria-label="Limpar busca">
                <FaTimes />
              </button>
            )}
          </div>

          <button className="btn-export" onClick={handleExportar}>
            <FaFileCsv /> Exportar CSV
          </button>
        </div>

        <div className="toolbar-row toolbar-row--secondary">
          <div className="date-field">
            <label htmlFor="dataInicio">De</label>
            <input type="date" id="dataInicio" value={dataInicio} onChange={(e) => handleDataInicio(e.target.value)} />
          </div>
          <div className="date-field">
            <label htmlFor="dataFim">Até</label>
            <input type="date" id="dataFim" value={dataFim} onChange={(e) => handleDataFim(e.target.value)} />
          </div>

          <div className="chips">
            {CATEGORIAS.map((c) => (
              <button
                key={c.termo}
                className={`chip ${categoriaAtiva === c.termo ? 'chip--active' : ''}`}
                onClick={() => aplicarCategoria(c.termo)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {temFiltros && (
          <div className="active-filters">
            <FaFilter className="active-filters__icon" />
            <span className="active-filters__label">Filtros ativos:</span>
            {buscaDebounced && (
              <span className="filter-tag">
                “{buscaDebounced}”
                <button onClick={() => setBusca('')} aria-label="Remover busca"><FaTimes /></button>
              </span>
            )}
            {dataInicio && (
              <span className="filter-tag">
                de {dataInicio.split('-').reverse().join('/')}
                <button onClick={() => handleDataInicio('')}><FaTimes /></button>
              </span>
            )}
            {dataFim && (
              <span className="filter-tag">
                até {dataFim.split('-').reverse().join('/')}
                <button onClick={() => handleDataFim('')}><FaTimes /></button>
              </span>
            )}
            <button className="active-filters__clear" onClick={limparTudo}>Limpar tudo</button>
          </div>
        )}
      </div>

      {error && <p className="logs-error">{error}</p>}

      {!error && (
        <>
          <div className="logs-table-wrapper">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Data e Hora</th>
                  <th>Usuário</th>
                  <th>Ação</th>
                  <th>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="skeleton-row">
                      <td><span className="skeleton" /></td>
                      <td><span className="skeleton" /></td>
                      <td><span className="skeleton skeleton--badge" /></td>
                      <td><span className="skeleton" /></td>
                    </tr>
                  ))
                ) : logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td className="cell-date">{formatarData(log.timestamp)}</td>
                      <td className="cell-user">{realcar(log.usuario_nome || 'Sistema', buscaDebounced)}</td>
                      <td>
                        <span className={`log-badge log-badge--${getAcaoVariant(log.acao)}`}>
                          {realcar(log.acao, buscaDebounced)}
                        </span>
                      </td>
                      <td className="cell-details">{realcar(log.detalhes, buscaDebounced)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-logs-message">
                      <div className="empty-state">
                        <FaSearch className="empty-state__icon" />
                        <strong>Nenhum log encontrado</strong>
                        <span>{temFiltros ? 'Tente ajustar a busca ou o período.' : 'Ainda não há registros no sistema.'}</span>
                        {temFiltros && <button className="btn-link" onClick={limparTudo}>Limpar filtros</button>}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalDePaginas > 0 && (
            <div className="logs-pagination">
              <span className="pagination-info">
                {totalDeLogs.toLocaleString('pt-BR')} registros no total
              </span>
              <div className="pagination-controls">
                <button onClick={irParaPaginaAnterior} disabled={paginaAtual === 1 || loading} className="pagination-button">
                  <FaChevronLeft /> Anterior
                </button>
                <span className="pagination-page-indicator">Página {paginaAtual} de {totalDePaginas}</span>
                <button onClick={irParaPaginaSeguinte} disabled={paginaAtual === totalDePaginas || loading} className="pagination-button">
                  Próximo <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}